import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { tokens } from '../../styles/tokens'
import { useAuth } from '../../hooks/useAuth'

interface Props {
  title: string
}

const HeaderWrapper = styled.header`
  height: 56px;
  background: white;
  border-bottom: 1px solid ${tokens.purple100};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px;
  flex-shrink: 0;
  position: relative;
  z-index: 10;
`

const Title = styled.h1`
  font-family: 'Cormorant Garamond', serif;
  font-size: 20px;
  font-weight: 400;
  color: ${tokens.purple900};
  letter-spacing: -0.3px;
`

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px 5px 5px;
  border-radius: 8px;
  border: 1px solid ${tokens.purple100};
  background: white;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  color: ${tokens.purple600};
  font-weight: 500;
  transition: background 0.15s;

  &:hover {
    background: ${tokens.purple50};
  }
`

const Avatar = styled.div`
  width: 26px;
  height: 26px;
  background: linear-gradient(135deg, ${tokens.purple600}, ${tokens.purple400});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: white;
  font-weight: 500;
  flex-shrink: 0;
`

const Chevron = styled.span`
  font-size: 9px;
  color: ${tokens.purple400};
  margin-left: 2px;
`

const Dropdown = styled.div<{ $open: boolean }>`
  position: absolute;
  top: calc(100% + 6px);
  right: 28px;
  width: 180px;
  background: white;
  border: 1px solid ${tokens.purple100};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(45, 27, 78, 0.1);
  overflow: hidden;
  opacity: ${p => (p.$open ? 1 : 0)};
  transform: ${p => (p.$open ? 'translateY(0)' : 'translateY(-6px)')};
  pointer-events: ${p => (p.$open ? 'all' : 'none')};
  transition:
    opacity 0.15s,
    transform 0.15s;
`

const DropdownHeader = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid ${tokens.purple100};
`

const DropdownEmail = styled.p`
  font-size: 11px;
  color: ${tokens.purple400};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DropdownItem = styled.button`
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  text-align: left;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  color: ${tokens.purple900};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.1s;

  &:hover {
    background: ${tokens.purple50};
  }
`

const DropdownDivider = styled.div`
  height: 1px;
  background: ${tokens.purple100};
  margin: 0;
`

const LogoutItem = styled(DropdownItem)`
  color: #c0436a;
`

export function Header({ title }: Props) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const initials = user?.name
    ? user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : (user?.email?.[0]?.toUpperCase() ?? '?')

  return (
    <HeaderWrapper>
      <Title>{title}</Title>

      <div ref={ref} style={{ position: 'relative' }}>
        <UserButton onClick={() => setOpen(o => !o)}>
          <Avatar>{initials}</Avatar>
          {user?.name ?? user?.email}
          <Chevron>▾</Chevron>
        </UserButton>

        <Dropdown $open={open}>
          <DropdownHeader>
            <DropdownEmail>{user?.email}</DropdownEmail>
          </DropdownHeader>

          <DropdownItem
            onClick={() => {
              navigate('/profile')
              setOpen(false)
            }}
          >
            Profile
          </DropdownItem>

          <DropdownDivider />

          <LogoutItem onClick={handleLogout}>Sign out</LogoutItem>
        </Dropdown>
      </div>
    </HeaderWrapper>
  )
}
